import React from 'react';
import { Box, Typography, TypographyProps, useTheme } from '@mui/material';
import HelpTooltipTrigger from './HelpTooltipTrigger';
import { getInlineHelpHint, type HelpValue } from './helpModel';
import { layoutGap } from '../../../theme/theme';

/**
 * Props for the LabelWithTooltip component
 */
export interface LabelWithTooltipProps {
  /** The label text to display */
  label: string;
  /** Optional tooltip text shown on hover */
  tooltip?: HelpValue;
  /** Preferred help prop */
  help?: HelpValue;
  /** Whether the label is for a required field */
  required?: boolean;
  /** Minimum width of the label container */
  minWidth?: number;
  /** Typography variant to use */
  variant?: TypographyProps['variant'];
  /** Additional CSS class name */
  className?: string;
  /** Custom ID for accessibility */
  id?: string;
  /** Inline hint rendered below the label when present */
  helperText?: string | null;
  /** Whether to render the inline hint from the help content */
  showInlineHint?: boolean;
  /** Optional element rendered inline with the label row */
  accessory?: React.ReactNode;
}

/**
 * A reusable label component with optional tooltip support.
 * Extracted from duplicated patterns across form components.
 *
 * @example
 * ```tsx
 * <LabelWithTooltip
 *   label="Username"
 *   tooltip="Enter your username"
 *   required
 * />
 * ```
 */
const LabelWithTooltip: React.FC<LabelWithTooltipProps> = ({
  label,
  help,
  tooltip,
  required = false,
  minWidth = 100,
  variant = 'body2',
  className,
  id,
  helperText,
  showInlineHint = false,
  accessory,
}) => {
  const theme = useTheme();
  const resolvedHelp = help ?? tooltip;
  const inlineHint = helperText ?? (showInlineHint ? getInlineHelpHint(resolvedHelp) : null);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: inlineHint ? layoutGap.dense : 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: layoutGap.row, minWidth: 0, width: '100%' }}>
        <Typography
          id={id}
          variant={variant}
          component="span"
          className={className}
          sx={{
            minWidth,
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            color: theme.palette.text.primary,
          }}
        >
          {label}
          {required && (
            <Typography
              component="span"
              sx={{
                color: theme.palette.error.main,
                ml: layoutGap.dense,
              }}
              aria-hidden="true"
            >
              *
            </Typography>
          )}
        </Typography>
        <HelpTooltipTrigger label={label} help={resolvedHelp} />
        {accessory && (
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', minWidth: 0 }}>
            {accessory}
          </Box>
        )}
      </Box>
      {inlineHint && (
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            lineHeight: 1.45,
            maxWidth: 520,
          }}
        >
          {inlineHint}
        </Typography>
      )}
    </Box>
  );
};

export default LabelWithTooltip;
